import {
  commentPosted,
  commentReactionAdded,
  commentsOpened,
  GameType,
  heardAboutSubmitted,
  loginCompleted,
  loginStarted,
  paymentFailedClicked,
  paymentFailedSeen,
  pushNotificationClicked,
  screenNameSet,
  signupSuccess,
  solvedCTACommunityClicked,
  solvedCTAShown,
  subscriptionPromptClicked,
  subscriptionPromptShown,
  type AnalyticsEvent,
  type Community,
  type HeardAboutAnswer,
  type ManageTarget,
  type ScreenNameSource,
  type SimpleDate,
  type SolvedCTAKind,
  type SubscriptionPromptPlacement,
} from '@puzzlio/core';
import { trackAllAnalyticsEvents } from './utils';
import type { SubscribeResult } from './push';

/** Sends an event built by the shared definitions in `@puzzlio/core/analytics`. */
const trackShared = ({ name, properties }: AnalyticsEvent, options?: { skipPosthog?: boolean; email?: string }) =>
  trackAllAnalyticsEvents(name, properties, options);

// Central definitions for the account-creation and subscription funnel events, so the event names and
// property shapes live in one place rather than as scattered string literals. All names are snake_case
// to match the existing events (puzzle_completion, subscription_success, ...).

// Where an event is shared with the mobile app, its name and properties are defined once in
// `@puzzlio/core/analytics` and built there — one funnel, two clients. What stays here is the
// fan-out, and the events only a browser can produce.
// `source` values are often read from a URL query param, so they're typed as plain strings. Known
// signup-wall sources: 'header' | 'subscribe' | 'settings' | 'solved' | 'play_cta' | 'game_gate' | 'direct'.
// Known paywall sources: 'highlighter' | 'archive' | 'homepage' | 'solved' | 'solved_cta' | 'game_card_lock' |
// 'account' | 'game_gate' | 'direct'.

export const trackSignupWallViewed = (source: string) => trackAllAnalyticsEvents('signupwall_viewed', { source });

// The create-account sheet shown over a puzzle a logged-out player opens (see useAccountCTA). Its
// Create Free Account button leads to the signup wall with source 'play_cta'.
export const trackAccountCTAShown = (gameType: GameType) =>
  trackAllAnalyticsEvents('account_cta_shown', { game_type: gameType });

// PostHog gets this from the server, at the point account creation is confirmed (auth/callback and
// verifyOtpLoginAndLinkUser), so the count doesn't depend on this screen surviving the /api/me
// round-trip. The ad pixels still fire here, where the browser context they need exists.
// `email` is passed for TikTok's advanced matching only; it is hashed before it leaves the browser.
export const trackSignupSuccess = (method: 'email' | 'google', email?: string) =>
  trackShared(signupSuccess(method), { skipPosthog: true, email });

export const trackLoginStarted = () => trackShared(loginStarted());

export const trackLoginCompleted = (method: 'email' | 'google') => trackShared(loginCompleted(method));

export const trackSubscriptionPromptShown = (gameType: GameType | undefined, placement: SubscriptionPromptPlacement) =>
  trackShared(subscriptionPromptShown(gameType, placement));

export const trackSubscriptionPromptClicked = (
  gameType: GameType | undefined,
  placement: SubscriptionPromptPlacement
) => trackShared(subscriptionPromptClicked(gameType, placement));

export const trackCommentsOpened = (gameType: GameType, boardDate: SimpleDate) =>
  trackShared(commentsOpened(gameType, boardDate));

export const trackCommentPosted = (gameType: GameType, boardDate: SimpleDate, isReply: boolean, hasPuzzle: boolean) =>
  trackShared(commentPosted(gameType, boardDate, isReply, hasPuzzle));

export const trackCommentReactionAdded = (gameType: GameType, boardDate: SimpleDate, emoji: string) =>
  trackShared(commentReactionAdded(gameType, boardDate, emoji));

export const trackScreenNameSet = (source: ScreenNameSource) => trackShared(screenNameSet(source));

export const trackPaymentFailedSeen = (target: ManageTarget) => trackShared(paymentFailedSeen(target));

export const trackPaymentFailedClicked = (target: ManageTarget) => trackShared(paymentFailedClicked(target));

/*
  `resumed` separates the two views a converting logged-out user generates: the first visit, and the
  return trip after the signup detour (which carries ?plan, and re-fires this on mount). It's always
  sent, so a breakdown splits cleanly into true/false -- an absent value means the event predates this
  property rather than "not resumed".
*/
export const trackPaywallViewed = (source: string, gameType?: GameType, resumed = false) =>
  trackAllAnalyticsEvents('paywall_viewed', gameType ? { source, game_type: gameType, resumed } : { source, resumed });

// Daily-reminder push funnel. `platform` separates iOS (which only reaches the prompt once the site
// is installed to the home screen) from everywhere else, since the two have very different funnels
// and the iOS install rate is the number that decides whether a native app is worth building.
export type PushPromptPlatform = 'ios_standalone' | 'other';

/** The two always-available controls, each of which can both enable and disable. */
export type PushSettingsSource = 'account' | 'settings';

/** Where an enable attempt started: the solved screen's rotating CTA, or one of the settings controls. */
export type PushEnableSource = 'solved_cta' | PushSettingsSource;

/**
 * One enable attempt, reported once the native permission dialog has settled so `result` is always
 * known — 'denied' is permanent for this origin. Only `result: 'subscribed'` is an actual enable;
 * the other outcomes are here so the drop-off is visible against the same source and platform.
 */
export const trackPushEnabled = (source: PushEnableSource, platform: PushPromptPlatform, result: SubscribeResult) =>
  trackAllAnalyticsEvents('push_enabled', { source, platform, result });

/**
 * The user turned daily reminders off from a settings control (as opposed to a browser-level
 * permission revoke, which we never see). The soft prompt cannot disable, so there is no 'prompt'
 * counterpart here.
 */
export const trackPushDisabled = (source: PushSettingsSource) => trackAllAnalyticsEvents('push_disabled', { source });

/**
 * A notification brought the user to the site. `campaign` is the notification's tag (the same value
 * that arrives as utm_campaign on the landing URL). `already_open` separates the service worker's two
 * paths: a real navigation (false), versus focusing a tab already on the target page (true) — the
 * latter is the common case on mobile and is the only signal we get, since no page load happens.
 */
export const trackPushNotificationClicked = (campaign: string, alreadyOpen: boolean) =>
  trackShared(pushNotificationClicked(campaign, alreadyOpen));

/**
 * Where the daily-reminders email opt-in happened. 'signup_success' is the checkbox on the
 * post-signup screen, 'account' the toggle on /auth/account, 'solved' the email form under a
 * finished puzzle (the only one an anonymous visitor can reach).
 */
export type DailyEmailSource = 'signup_success' | 'account' | 'solved';

/**
 * The daily-reminders email opt-in. On 'signup_success' it fires on every exit from that screen,
 * with `opted_in` carrying the answer, so the denominator is the whole cohort that saw the checkbox
 * and the rate is readable off this one event; the other two sources have no such captive cohort,
 * so they only fire when the user acts. Reports the user's choice, not the outcome of the Resend
 * call — a failed subscribe is still a "yes" for funnel purposes. `method` is the signup method and
 * only exists on 'signup_success', where the opt-in is part of account creation.
 */
export const trackDailyEmailEnabled = (source: DailyEmailSource, optedIn: boolean, method?: 'email' | 'google') =>
  trackAllAnalyticsEvents(
    'daily_email_enabled',
    method ? { source, opted_in: optedIn, method } : { source, opted_in: optedIn }
  );

export const trackSolvedCTAShown = (cta: SolvedCTAKind) => trackShared(solvedCTAShown(cta));

export const trackSolvedCTACommunityClicked = (community: Community) =>
  trackShared(solvedCTACommunityClicked(community));

export const trackHeardAboutSubmitted = (answer: HeardAboutAnswer) => trackShared(heardAboutSubmitted(answer));

/** A blog post was opened. Fired from the page, so a reader arriving from search counts too. */
export const trackBlogPostViewed = (slug: string, category: string | null) =>
  trackAllAnalyticsEvents('blog_post_viewed', { slug, category });

/** A teaser card was clicked: on the home page, the blog's featured block or list, or the end of a post. */
export const trackBlogCardClicked = (slug: string, placement: BlogCardPlacement) =>
  trackAllAnalyticsEvents('blog_card_clicked', { slug, placement });

export type BlogCardPlacement = 'home' | 'blog_featured' | 'blog_list' | 'post_more';
